import type { ThreadManagementAction, OrchestrationEvent } from "@afar-research/contracts";
import type {
  OrchestrationThread,
  OrchestrationThreadShell,
  ThreadManagement,
} from "@afar-research/contracts";
import { deriveThreadAttentionRequests } from "./threadAttention.ts";
import { isThreadBusy } from "./threadExecution.ts";

export type { ThreadManagementAction } from "@afar-research/contracts";
export const emptyThreadManagement: ThreadManagement = {
  settledAt: null,
  snoozedAt: null,
  snoozedUntil: null,
};

export function canSnoozeThread(
  thread: Pick<OrchestrationThreadShell, "hasPendingApprovals" | "hasPendingUserInput" | "session">,
): boolean {
  return (
    !thread.hasPendingApprovals &&
    !thread.hasPendingUserInput &&
    thread.session?.status !== "starting"
  );
}

/** Admission reads the thread's attention requests and its live session. */
export function threadManagementActionError(
  thread: OrchestrationThread,
  action: ThreadManagementAction,
  until: string | undefined,
  now: string,
): string | null {
  if (action === "restore" || action === "unsnooze") return null;
  const requests = deriveThreadAttentionRequests(thread.activities);
  if (
    [...requests.approvals, ...requests.userInputs].some(
      (request) => request.status === "unresolved",
    )
  ) {
    return "Respond to the thread's pending approval or input before putting it away.";
  }
  if (
    action === "settle" &&
    (thread.session?.status === "running" ||
      isThreadBusy({
        sessionStatus: thread.session?.status,
        latestTurnState: thread.latestTurn?.state,
      }))
  ) {
    return "Wait for the thread to finish before settling it.";
  }
  if (action === "snooze" && thread.session?.status === "starting")
    return "Wait for the thread to start before snoozing it.";
  if (
    action === "snooze" &&
    (until === undefined ||
      !Number.isFinite(Date.parse(until)) ||
      Date.parse(until) <= Date.parse(now))
  )
    return "Choose a snooze time in the future.";
  return null;
}

export function updateThreadManagement(
  current: ThreadManagement | null | undefined,
  action: ThreadManagementAction,
  now: string,
  until?: string,
): ThreadManagement {
  switch (action) {
    case "settle":
      return { ...emptyThreadManagement, settledAt: now };
    case "snooze":
      return { settledAt: null, snoozedAt: now, snoozedUntil: until ?? null };
    case "restore":
      return { ...emptyThreadManagement };
    case "unsnooze":
      return { ...emptyThreadManagement, settledAt: current?.settledAt ?? null };
  }
}

type ManagementThread = Partial<
  Pick<
    OrchestrationThreadShell,
    | "management"
    | "latestTurn"
    | "session"
    | "latestUserMessageAt"
    | "hasPendingApprovals"
    | "hasPendingUserInput"
  >
>;
const timestamp = (value: string | null | undefined) => (value ? Date.parse(value) || 0 : 0);

/**
 * The newest instant that counts as the thread doing something. Parking is
 * measured against this: a snooze or a settle only holds while nothing has
 * happened since it was set.
 */
function threadActivityAt(thread: ManagementThread): number {
  const engagement = Math.max(
    timestamp(thread.latestUserMessageAt),
    timestamp(thread.latestTurn?.requestedAt),
    timestamp(thread.latestTurn?.startedAt),
  );
  return Math.max(
    engagement,
    timestamp(thread.latestTurn?.completedAt),
    thread.session?.status === "error" ? timestamp(thread.session.updatedAt) : 0,
  );
}

/** Time expiry is derived from persisted instants, so disconnected clients and restarts need no wake job. */
export function threadManagementSection(
  thread: ManagementThread,
  now: number,
): "active" | "snoozed" | "settled" {
  const management = thread.management;
  if (!management || thread.hasPendingApprovals || thread.hasPendingUserInput) return "active";
  const activity = threadActivityAt(thread);
  if (
    management.snoozedAt &&
    management.snoozedUntil &&
    now < timestamp(management.snoozedUntil) &&
    activity <= timestamp(management.snoozedAt)
  )
    return "snoozed";
  if (
    management.settledAt &&
    activity <= timestamp(management.settledAt) &&
    thread.latestTurn?.state !== "running" &&
    thread.session?.status !== "starting" &&
    thread.session?.status !== "running"
  )
    return "settled";
  return "active";
}

/**
 * When a snoozed thread came back, or null if it never snoozed or is still
 * snoozed.
 *
 * A woken thread returns to its original place in the list rather than jumping
 * to the top, because the order is deliberately stable — so the wake signal has
 * to carry the weight of saying it is back. Clear it by comparing against the
 * client's last visit: opening the thread dismisses it, the way it does unread.
 *
 * A thread that raised its hand early reports what woke it, not its scheduled
 * wake time, so a visit made BEFORE the early wake cannot suppress the signal.
 * That stays authoritative even once the scheduled time passes: reporting
 * `snoozedUntil` then would resurface a signal the user already cleared.
 */
export function threadWokeAt(thread: ManagementThread, now: number): string | null {
  const management = thread.management;
  if (!management?.snoozedUntil) return null;
  const wakeAtMs = Date.parse(management.snoozedUntil);
  // Malformed data never claims a thread woke.
  if (!Number.isFinite(wakeAtMs)) return null;
  if (threadActivityAt(thread) > timestamp(management.snoozedAt)) {
    const completedAt = thread.latestTurn?.completedAt;
    if (
      management.snoozedAt &&
      thread.latestTurn?.state === "completed" &&
      completedAt &&
      timestamp(completedAt) > timestamp(management.snoozedAt)
    ) {
      return completedAt;
    }
    return thread.session?.updatedAt ?? management.snoozedAt;
  }
  if (thread.hasPendingApprovals || thread.hasPendingUserInput) {
    return thread.session?.updatedAt ?? management.snoozedAt;
  }
  return wakeAtMs <= now ? management.snoozedUntil : null;
}

export function threadWorkingStartedAt(
  thread: Pick<OrchestrationThreadShell, "latestTurn" | "session">,
): string | null {
  if (thread.latestTurn?.state !== "running") return null;
  return thread.latestTurn.startedAt ?? thread.latestTurn.requestedAt;
}

/** User work and required attention reopen a thread even when an event carries an older timestamp. */
export function threadManagementResetsOnEvent(event: OrchestrationEvent): boolean {
  return (
    event.type === "thread.turn-start-requested" ||
    (event.type === "thread.session-set" && event.payload.session.status === "error") ||
    (event.type === "thread.message-sent" && event.payload.role === "user") ||
    (event.type === "thread.activity-appended" &&
      ["thread.queue.entry.accepted", "approval.requested", "user-input.requested"].includes(
        event.payload.activity.kind,
      ))
  );
}
